/* eslint-disable @typescript-eslint/naming-convention, complexity, max-lines-per-function */
import React, {FC} from 'react';
import dynamic from 'next/dynamic';
import {Box, Col, Row} from 'components/Grid';
import {Breadcrumbs} from 'modules/breadcrumbs/components/Breadcrumbs';
import {IBreadcrumb} from 'modules/seo/types';
import {CatalogProductList} from 'modules/products/components/CatalogProductList';
import {InfoSeoTitle} from 'modules/products/pages/CatalogPage/components/InfoSeoTitle';
import {InterestingSections} from 'modules/products/components/InterestingSections';
import {CatalogListHeader} from 'modules/products/components/CatalogListHeader';
import {CatalogSeoTextBottom} from 'modules/products/components/CatalogSeoTextBottom';
import {CatalogListHeaderSticky} from 'modules/products/components/CatalogListHeaderSticky';
import {TrustSlider} from 'modules/slider/components/TrustSlider';
import {Filters} from 'plugins/modules/filter/components/Filters/LazyFilters';
import {ComboFilters} from 'plugins/modules/filter/components/ComboFilters';
import {ModalLazyLoader} from 'components/ModalLazyLoader';
import {PageHead} from 'core/modules/PageHead';
import {SkeletonWrapper} from 'modules/skeleton/components/SkeletonWrapper';
import style from './style.module.scss';
import {ClientNotMobile} from 'components/ClientNotMobile';
import {ClientDesktopOnly} from 'components/ClientDesktopOnly';
import Head from 'next/head';
import {MODALS} from 'modules/modals/constants';
import {Modal} from 'modules/modals/components/Modal';
import {useCatalogPageState} from 'modules/products/pages/CatalogPage/hooks/useCatalogPageState';
import {metaData} from 'core/modules/PageHead/constants';
import {isUrlChangedIgnoringFilters} from 'plugins/modules/filter/helpers';

const FiltersModalMobile = dynamic(() => import('plugins/modules/filter/components/FiltersModalMobile'), {
    loading: () => <ModalLazyLoader />,
    ssr: false,
});

interface ICatalogPageContentProps {
    breadcrumbs: IBreadcrumb[];
}

export const CatalogPageContent: FC<ICatalogPageContentProps> = ({breadcrumbs: initialBreadcrumbs}) => {
    const {
        hasInterestingSections,
        productSchemaJson,
        isFirstPage,
        ogUrl,
        ogImage,
        isFetching,
        isLoading,
        seo,
        minPrice,
        maxPrice,
        appliedFilters,
        pagination,
        meta,
        breadcrumbs,
    } = useCatalogPageState(initialBreadcrumbs);

    return (
        <>
            <PageHead
                description={seo?.metaDescription || metaData.home.description}
                imageUrl={ogImage}
                ogUrl={ogUrl}
                robots={seo?.robots}
                title={seo?.metaTitle || metaData.home.title}
            />

            {productSchemaJson && (
                <Head>
                    <script
                        type={'application/ld+json'}
                        dangerouslySetInnerHTML={{__html: JSON.stringify(productSchemaJson)}}
                    />
                </Head>
            )}

            <BreadcrumbsSection
                breadcrumbsList={breadcrumbs}
                isFetching={isFetching}
                hasFilters={0 < appliedFilters.length}
            />

            <SkeletonWrapper disabled={!isFetching}>
                <InfoSeoTitle seo={seo} total={pagination.totalCount} />
            </SkeletonWrapper>

            {meta?.combos && <ComboFiltersSection />}

            <div className={style.mobileHeader}>
                <CatalogListHeader />
            </div>

            <ProductListingSection isLoading={isLoading} />

            {hasInterestingSections && (
                <Box className={style.interestingRow} commonPaddingOnMobileTablet={true}>
                    <InterestingSections />
                </Box>
            )}

            {isFirstPage && (
                <CatalogSeoTextBottom
                    className={style.seoTextBottom}
                    categoryName={seo?.pageTitle || ''}
                    text={seo?.catalogSeoTextLower || ''}
                    minPrice={minPrice}
                    maxPrice={maxPrice}
                />
            )}

            <TrustSlider />

            <ModalsSection />
        </>
    );
};

const BreadcrumbsSection: FC<{
    breadcrumbsList: IBreadcrumb[];
    isFetching: boolean;
    hasFilters: boolean;
}> = ({breadcrumbsList, isFetching, hasFilters}) => (
    <Box>
        <Row>
            <Col sm={12}>
                <SkeletonWrapper disabled={!isFetching}>
                    <Breadcrumbs breadcrumbs={breadcrumbsList} enableLastLink={hasFilters} />
                </SkeletonWrapper>
            </Col>
        </Row>
    </Box>
);

const ComboFiltersSection = () => (
    <Box className={style.comboFiltersWrapper}>
        <div className={style.comboFilters}>
            <ComboFilters />
        </div>
    </Box>
);

const ProductListingSection: FC<{
    isLoading?: boolean;
}> = ({isLoading}) => (
    <Box className={style.listBox}>
        <SkeletonWrapper className={style.desktopCol} disabled={!isLoading}>
            <ClientDesktopOnly>
                <Filters />
            </ClientDesktopOnly>
        </SkeletonWrapper>
        <div className={style.contentProduct}>
            <ClientNotMobile>
                <CatalogListHeaderSticky />
            </ClientNotMobile>
            <CatalogProductList />
        </div>
    </Box>
);

const ModalsSection: FC = () => (
    <>
        <Modal
            disableOnPageLoading={false}
            name={MODALS.FILTER_MODAL_MOBILE.name}
            shouldCloseOnUrlChange={isUrlChangedIgnoringFilters}
            willMount={true}
        >
            <FiltersModalMobile />
        </Modal>
    </>
);
